import {
  AlarmClock,
  ArrowDownLeft,
  ArrowUpRight,
  CalendarClock,
  CalendarRange,
  CircleAlert,
  CreditCard,
  History,
  ListChecks,
  MoreHorizontal,
  Pencil,
  Plus,
  Redo2,
  SkipForward,
  Trash2,
  Undo2,
  Wallet,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { InfoNote } from '@/components/common/Panel'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Badge, type BadgeTone } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { confirmModal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAuth } from '@/lib/auth/AuthProvider'
import { cn } from '@/lib/utils'
import { fmtDate, fmtMoney } from '../shared/format'
import {
  useDeleteObligation,
  useObligationItems,
  useObligations,
  useObligationSummary,
  useSkipObligationItem,
} from './api'
import { Kpi, Money, Section } from './components/FinanceBits'
import { ObligationFormModal } from './components/ObligationFormModal'
import { PastPaymentModal, PayPastModal } from './components/ObligationPastModals'
import { ObligationPayModal } from './components/ObligationPayModal'
import { FINANCE_OBLIGATION_MANAGE, FINANCE_PAYMENT_WRITE } from './permissions'
import type { Obligation, ObligationItem } from './types'
import { monthLabel, toastError, useFinanceLookups } from './ui'
import { useRequestVoid } from './voids'

type View = 'schedule' | 'definitions'
type StatusFilter = 'open' | 'paid' | 'skipped' | 'all'
type DirFilter = 'all' | 'out' | 'in'

const dueTone = (it: ObligationItem): BadgeTone =>
  it.status === 'paid'
    ? 'success'
    : it.status === 'skipped'
      ? 'outline'
      : it.daysLeft < 0
        ? 'danger'
        : it.daysLeft <= 3
          ? 'warning'
          : 'info'

/** Kira, SGK, vergi, abonelik gibi düzenli ödemeler ile beklenen gelirlerin vade takvimi ve tanımları. */
export function ObligationsPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const canManage = can(FINANCE_OBLIGATION_MANAGE)
  const canPay = canManage && can(FINANCE_PAYMENT_WRITE)
  const summary = useObligationSummary()
  const items = useObligationItems()
  const defs = useObligations()
  const [view, setView] = useState<View>('schedule')
  const [status, setStatus] = useState<StatusFilter>('open')
  const [dir, setDir] = useState<DirFilter>('all')
  const [editing, setEditing] = useState<Obligation | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [paying, setPaying] = useState<ObligationItem | null>(null)
  const [pastOpen, setPastOpen] = useState(false)
  const [payingPast, setPayingPast] = useState<Obligation | null>(null)
  const s = summary.data

  const openForm = (o: Obligation | null) => {
    setEditing(o)
    setFormOpen(true)
  }

  return (
    <>
      <PageHeader
        eyebrow={t('nav.groups.finance')}
        title={t('finance.obligations.title')}
        description={t('finance.obligations.subtitle')}
        actions={
          canManage ? (
            <div className="flex flex-wrap gap-2">
              {canPay ? (
                <Button variant="outline" onClick={() => setPastOpen(true)}>
                  <History />
                  {t('finance.obligations.past.open')}
                </Button>
              ) : null}
              <Button onClick={() => openForm(null)}>
                <Plus />
                {t('finance.obligations.new')}
              </Button>
            </div>
          ) : null
        }
      />
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
          <Kpi
            icon={CircleAlert}
            tone={s && s.overdueCount > 0 ? 'danger' : 'neutral'}
            label={t('finance.obligations.kpi.overdue')}
            value={s ? fmtMoney(s.overdueAmount) : <Skeleton className="h-7 w-24" />}
            footer={s ? t('finance.obligations.kpi.items', { count: s.overdueCount }) : null}
          />
          <Kpi
            icon={Wallet}
            tone="warning"
            label={t('finance.obligations.kpi.monthOut')}
            value={s ? fmtMoney(s.monthOut) : <Skeleton className="h-7 w-24" />}
            footer={s ? t('finance.obligations.kpi.monthPaid', { amount: fmtMoney(s.monthPaid) }) : null}
          />
          <Kpi
            icon={AlarmClock}
            tone="info"
            label={t('finance.obligations.kpi.next30Out')}
            value={s ? fmtMoney(s.next30Out) : <Skeleton className="h-7 w-24" />}
            footer={s ? t('finance.obligations.kpi.next7', { count: s.next7Count }) : null}
          />
          <Kpi
            icon={ArrowDownLeft}
            tone="success"
            label={t('finance.obligations.kpi.next30In')}
            value={s ? fmtMoney(s.next30In) : <Skeleton className="h-7 w-24" />}
          />
        </div>
        <InfoNote>{t('finance.obligations.info')}</InfoNote>

        <Tabs value={view} onValueChange={(v) => setView(v as View)}>
          <TabsList>
            <TabsTrigger value="schedule">
              <CalendarRange />
              {t('finance.obligations.tabs.schedule')}
            </TabsTrigger>
            <TabsTrigger value="definitions">
              <ListChecks />
              {t('finance.obligations.tabs.definitions')}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {view === 'schedule' ? (
          <Schedule
            query={items}
            status={status}
            dir={dir}
            onStatus={setStatus}
            onDir={setDir}
            canManage={canManage}
            canPay={canPay}
            onPay={setPaying}
          />
        ) : (
          <Definitions query={defs} canManage={canManage} canPay={canPay} onEdit={openForm} onPayPast={setPayingPast} />
        )}
      </div>
      <ObligationFormModal open={formOpen} obligation={editing} onOpenChange={setFormOpen} />
      <ObligationPayModal item={paying} onClose={() => setPaying(null)} />
      <PastPaymentModal open={pastOpen} onClose={() => setPastOpen(false)} />
      <PayPastModal key={payingPast?.id ?? 'none'} obligation={payingPast} onClose={() => setPayingPast(null)} />
    </>
  )
}

function Schedule({
  query,
  status,
  dir,
  onStatus,
  onDir,
  canManage,
  canPay,
  onPay,
}: {
  query: ReturnType<typeof useObligationItems>
  status: StatusFilter
  dir: DirFilter
  onStatus: (v: StatusFilter) => void
  onDir: (v: DirFilter) => void
  canManage: boolean
  canPay: boolean
  onPay: (it: ObligationItem) => void
}) {
  const { t } = useTranslation()
  const skip = useSkipObligationItem()
  const voids = useRequestVoid()
  const groups = useMemo(() => {
    const rows = (query.data ?? []).filter(
      (it) =>
        (dir === 'all' || it.direction === dir) &&
        (status === 'all' || (status === 'open' ? it.status === 'pending' : it.status === status)),
    )
    // Açık kalemlerde gecikmişler ilk grupta toplanır; diğer kalemler vade ayına göre gruplanır.
    const map = new Map<string, ObligationItem[]>()
    for (const it of rows) {
      const key = it.status === 'pending' && it.overdue ? 'overdue' : it.dueDate.slice(0, 7)
      map.set(key, [...(map.get(key) ?? []), it])
    }
    const keys = [...map.keys()].sort((a, b) =>
      a === 'overdue' ? -1 : b === 'overdue' ? 1 : status === 'open' ? a.localeCompare(b) : b.localeCompare(a),
    )
    return keys.map((k) => ({ key: k, rows: map.get(k) ?? [] }))
  }, [query.data, status, dir])

  const doUnpay = (it: ObligationItem) => voids.request('obligation_item', it.id, t('finance.obligations.confirm.unpayBody', { title: it.title, period: it.period }))
  const doSkip = (it: ObligationItem, value: boolean) =>
    skip.mutate(
      { id: it.id, skip: value },
      {
        onSuccess: () => toast.success(t(value ? 'finance.obligations.skipped' : 'finance.obligations.unskipped')),
        onError: toastError,
      },
    )

  return (
    <Section
      title={t('finance.obligations.tabs.schedule')}
      icon={CalendarClock}
      bodyClassName="p-0"
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <Select
            size="sm"
            className="w-36"
            value={dir}
            onChange={(v) => onDir(v as DirFilter)}
            aria-label={t('finance.obligations.form.direction')}
            options={[
              { value: 'all', label: t('common.all') },
              { value: 'out', label: t('finance.obligations.direction.out') },
              { value: 'in', label: t('finance.obligations.direction.in') },
            ]}
          />
          <Select
            size="sm"
            className="w-36"
            value={status}
            onChange={(v) => onStatus(v as StatusFilter)}
            aria-label={t('finance.tx.status')}
            options={(['open', 'paid', 'skipped', 'all'] as const).map((v) => ({
              value: v,
              label: t(`finance.obligations.filter.${v}`),
            }))}
          />
        </div>
      }
    >
      {query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : query.isLoading ? (
        <div className="space-y-2 p-4">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-14" />
          ))}
        </div>
      ) : groups.length === 0 ? (
        <EmptyState icon={CalendarClock} title={t('finance.obligations.emptySchedule')} />
      ) : (
        <div className="divide-y">
          {groups.map((g) => {
            const out = g.rows.filter((r) => r.direction === 'out').reduce((a, r) => a + Number(r.amount), 0)
            const inc = g.rows.filter((r) => r.direction === 'in').reduce((a, r) => a + Number(r.amount), 0)
            return (
              <div key={g.key}>
                <div
                  className={cn(
                    'bg-muted/40 flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2 text-xs font-semibold tracking-wide uppercase',
                    g.key === 'overdue' ? 'text-destructive' : 'text-muted-foreground',
                  )}
                >
                  <span className="flex-1">
                    {g.key === 'overdue' ? t('finance.obligations.overdueGroup') : monthLabel(g.key)}
                  </span>
                  {out ? (
                    <span className="normal-case tabular-nums">
                      {t('finance.obligations.groupOut', { amount: fmtMoney(out.toFixed(2)) })}
                    </span>
                  ) : null}
                  {inc ? (
                    <span className="normal-case tabular-nums">
                      {t('finance.obligations.groupIn', { amount: fmtMoney(inc.toFixed(2)) })}
                    </span>
                  ) : null}
                </div>
                <ul className="divide-y">
                  {g.rows.map((it) => (
                    <li
                      key={it.id}
                      className={cn(
                        'flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:gap-4',
                        it.status === 'skipped' && 'opacity-60',
                      )}
                    >
                      <div className="flex w-14 shrink-0 flex-col items-center rounded-lg border py-1 text-center">
                        <span className="text-lg leading-none font-semibold tabular-nums">
                          {it.dueDate.slice(8, 10)}
                        </span>
                        <span className="text-muted-foreground text-[11px] uppercase">
                          {monthLabel(it.dueDate.slice(0, 7), 'LLL')}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          {it.direction === 'in' ? (
                            <ArrowDownLeft className="text-success size-4" aria-hidden />
                          ) : (
                            <ArrowUpRight className="text-muted-foreground size-4" aria-hidden />
                          )}
                          <span className={cn('truncate font-medium', it.status === 'skipped' && 'line-through')}>
                            {it.title}
                          </span>
                          <Badge tone="outline" className="rounded-md">
                            {t(`finance.obligations.kinds.${it.kind}`)}
                          </Badge>
                          {it.locked ? (
                            <Badge tone="neutral" className="rounded-md">
                              {t('finance.obligations.locked')}
                            </Badge>
                          ) : null}
                        </div>
                        <p className="text-muted-foreground truncate text-xs">
                          {t('finance.obligations.periodLabel', { period: it.period })} · {t('finance.obligations.due')}{' '}
                          {fmtDate(it.dueDate)}
                          {it.status === 'paid' && it.paidOn
                            ? ` · ${t('finance.obligations.paidOn', { date: fmtDate(it.paidOn) })}`
                            : ''}
                          {it.note ? ` · ${it.note}` : ''}
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                        <Badge tone={dueTone(it)} className="whitespace-nowrap">
                          {it.status === 'paid'
                            ? t(
                                it.direction === 'in'
                                  ? 'finance.obligations.status.collected'
                                  : 'finance.obligations.status.paid',
                              )
                            : it.status === 'skipped'
                              ? t('finance.obligations.status.skipped')
                              : it.daysLeft < 0
                                ? t('finance.tax.calendar.late', { count: -it.daysLeft })
                                : it.daysLeft === 0
                                  ? t('finance.obligations.today')
                                  : t('finance.tax.calendar.daysLeft', { count: it.daysLeft })}
                        </Badge>
                        <Money value={it.amount} className="w-28 text-right font-semibold" />
                        {canPay && it.status === 'pending' ? (
                          <Button
                            size="sm"
                            variant={it.overdue || it.daysLeft <= 3 ? 'default' : 'outline'}
                            onClick={() => onPay(it)}
                          >
                            <CreditCard />
                            {t(
                              it.direction === 'in'
                                ? 'finance.obligations.actions.collect'
                                : 'finance.obligations.actions.pay',
                            )}
                          </Button>
                        ) : null}
                        {canManage ? (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-muted-foreground size-8"
                                aria-label={t('finance.rowActions', { name: it.title })}
                              >
                                <MoreHorizontal />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="min-w-48">
                              {it.status === 'pending' ? (
                                <DropdownMenuItem onSelect={() => doSkip(it, true)}>
                                  <SkipForward />
                                  {t('finance.obligations.actions.skip')}
                                </DropdownMenuItem>
                              ) : null}
                              {it.status === 'skipped' ? (
                                <DropdownMenuItem onSelect={() => doSkip(it, false)}>
                                  <Redo2 />
                                  {t('finance.obligations.actions.unskip')}
                                </DropdownMenuItem>
                              ) : null}
                              {it.status === 'paid' && canPay ? (
                                <DropdownMenuItem
                                  onSelect={() => void doUnpay(it)}
                                  className="text-destructive [&_svg]:text-destructive"
                                >
                                  <Undo2 />
                                  {t('finance.obligations.actions.unpay')}
                                </DropdownMenuItem>
                              ) : null}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )
          })}
        </div>
      )}
    </Section>
  )
}

function Definitions({
  query,
  canManage,
  canPay,
  onEdit,
  onPayPast,
}: {
  query: ReturnType<typeof useObligations>
  canManage: boolean
  canPay: boolean
  onEdit: (o: Obligation) => void
  onPayPast: (o: Obligation) => void
}) {
  const { t } = useTranslation()
  const del = useDeleteObligation()
  const { catName, partyName } = useFinanceLookups()
  const rows = query.data ?? []

  const remove = async (o: Obligation) => {
    const ok = await confirmModal({
      title: t('finance.obligations.confirm.deleteTitle'),
      description: t('finance.obligations.confirm.deleteBody', { title: o.title }),
      confirmLabel: t('common.delete'),
      destructive: true,
    })
    if (!ok) return
    del.mutate(o.id, {
      onSuccess: (res) =>
        toast.success(t(res?.deleted ? 'finance.obligations.deleted' : 'finance.obligations.deactivated')),
      onError: toastError,
    })
  }

  const schedule = (o: Obligation) =>
    o.recurrence === 'none'
      ? t('finance.obligations.once', { date: fmtDate(o.startDate) })
      : t(`finance.obligations.every.${o.recurrence}`, { day: o.dueDay })

  return (
    <Section title={t('finance.obligations.tabs.definitions')} icon={ListChecks} bodyClassName="p-0">
      {query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : query.isLoading ? (
        <div className="space-y-2 p-4">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-14" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState icon={ListChecks} title={t('finance.obligations.emptyDefinitions')} />
      ) : (
        <ul className="divide-y">
          {rows.map((o) => (
            <li
              key={o.id}
              className={cn(
                'flex flex-col gap-2 px-4 py-3 lg:flex-row lg:items-center lg:gap-4',
                !o.active && 'opacity-60',
              )}
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  {o.direction === 'in' ? (
                    <ArrowDownLeft className="text-success size-4" aria-hidden />
                  ) : (
                    <ArrowUpRight className="text-muted-foreground size-4" aria-hidden />
                  )}
                  <span className="truncate font-medium">{o.title}</span>
                  <Badge tone="outline" className="rounded-md">
                    {t(`finance.obligations.kinds.${o.kind}`)}
                  </Badge>
                  {o.source === 'payroll' ? (
                    <Badge tone="info" className="rounded-md">
                      {t('finance.obligations.fromPayroll')}
                    </Badge>
                  ) : null}
                  {!o.active ? <Badge tone="neutral">{t('finance.obligations.inactive')}</Badge> : null}
                </div>
                <p className="text-muted-foreground truncate text-xs">
                  {schedule(o)} · {catName(o.categoryId)}
                  {o.partyId ? ` · ${partyName(o.partyId)}` : ''}
                  {o.nextDue ? ` · ${t('finance.obligations.nextDue', { date: fmtDate(o.nextDue) })}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-2 lg:justify-end">
                {o.source === 'payroll' ? (
                  <span className="text-muted-foreground text-xs">{t('finance.obligations.payrollAmount')}</span>
                ) : (
                  <Money value={o.currentAmount} className="font-semibold" />
                )}
                {canPay && o.source !== 'payroll' && o.active ? (
                  <Button size="sm" variant="outline" onClick={() => onPayPast(o)}>
                    <History />
                    <span className="hidden sm:inline">{t('finance.obligations.payPast.open')}</span>
                  </Button>
                ) : null}
                {canManage ? (
                  <>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="size-8"
                      aria-label={t('common.edit')}
                      onClick={() => onEdit(o)}
                    >
                      <Pencil />
                    </Button>
                    {o.source !== 'payroll' ? (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="text-destructive hover:text-destructive size-8"
                        aria-label={t('common.delete')}
                        disabled={del.isPending}
                        onClick={() => void remove(o)}
                      >
                        <Trash2 />
                      </Button>
                    ) : null}
                  </>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Section>
  )
}
